import React, { useState, useEffect } from 'react';
import CustomAlert from '../components/CustomAlert';
import useCustomAlert from '../hooks/useCustomAlert';
import { updateUserPassword } from '../services/firestoreService';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import '../styles/MyProfile.css';

const MyProfile = ({ user }) => {
  const { alertState, showSuccess, showError, showWarning, showConfirm, showInfo } = useCustomAlert();
  const [isEditing, setIsEditing] = useState(false);
  const [localUser, setLocalUser] = useState(user);
  
  // 전화번호 포맷팅 함수 (010-1234-5678 형식으로 변환)
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    // 숫자만 추출
    const numbers = phone.replace(/[^0-9]/g, '');
    
    // 11자리 숫자인 경우에만 포맷팅
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    
    // 10자리 숫자인 경우 (010으로 시작하는 경우)
    if (numbers.length === 10 && numbers.startsWith('010')) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    }
    
    // 그 외의 경우는 원본 반환
    return phone;
  };
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    hasCompetition: user?.hasCompetition || false
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // user prop이 변경될 때 및 Firestore 변경 반영 (경시 이력 표시 정확도 향상)
  useEffect(() => {
    setLocalUser(user);
    setFormData(prev => ({
      ...prev,
      hasCompetition: user?.hasCompetition || false
    }));
  }, [user]);

  // 실시간 사용자 정보 반영 (경시 이력 포함)
  useEffect(() => {
    if (!user?.id) return;
    const unsub = onSnapshot(doc(db, 'users', user.id), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setFormData(prev => ({
          ...prev,
          name: data?.name ?? prev.name,
          phone: data?.phone ?? prev.phone,
          email: data?.email ?? prev.email,
          hasCompetition: Boolean(data?.hasCompetition)
        }));
      }
    });
    return () => unsub();
  }, [user?.id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // 전화번호인 경우 포맷팅 적용
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSave = () => {
    // TODO: 실제 API 호출로 데이터 저장
    console.log('저장될 데이터:', formData);
    showSuccess('정보가 성공적으로 수정되었습니다.');
    setIsEditing(false);
  };

  const handleCancel = () => {
    // 원래 데이터로 되돌리기
    setFormData(prev => ({
      ...prev,
      name: user?.name || '',
      phone: user?.phone || '',
      email: user?.email || '',
      // 경시 이력은 서버 상태(onSnapshot)로 유지: 별도 리셋하지 않음
    }));
    setIsEditing(false);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // 입력 검증
    if (!passwordData.currentPassword.trim()) {
      showError('현재 비밀번호를 입력해주세요.');
      setLoading(false);
      return;
    }

    if (!passwordData.newPassword.trim()) {
      showError('새 비밀번호를 입력해주세요.');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showError('새 비밀번호는 최소 6자 이상이어야 합니다.');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError('새 비밀번호가 일치하지 않습니다.');
      setLoading(false);
      return;
    }

    try {
      // 현재 비밀번호 확인
      if (user.password !== passwordData.currentPassword) {
        showError('현재 비밀번호가 일치하지 않습니다.');
        setLoading(false);
        return;
      }

      // 비밀번호 업데이트
      await updateUserPassword(user.id, passwordData.newPassword);
      
      showSuccess('비밀번호가 성공적으로 변경되었습니다.');
      setShowPasswordModal(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('비밀번호 변경 오류:', error);
      showError('비밀번호 변경에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>👤 내 정보</h1>
        <p>개인정보를 확인하고 수정하세요</p>
      </div>

      <div className="profile-content">
        {/* 기본 정보 카드 */}
        <div className="profile-card">
          <div className="card-header">
            <h3>📋 기본 정보</h3>
            {!isEditing ? (
              <button className="edit-btn" onClick={() => setIsEditing(true)}>
                ✏️ 수정하기
              </button>
            ) : (
              <div className="edit-actions">
                <button className="cancel-btn" onClick={handleCancel}>
                  취소
                </button>
                <button className="save-btn" onClick={handleSave}>
                  💾 저장
                </button>
              </div>
            )}
          </div>

          <div className="profile-form">
            

            <div className="form-group">
              <label>비밀번호</label>
              <div className="password-display">
                <input 
                  type="password" 
                  value="••••••••" 
                  readOnly 
                  className="readonly-input"
                />
                <button 
                  type="button" 
                  className={`change-btn ${!isEditing ? 'disabled' : ''}`}
                  onClick={() => isEditing && setShowPasswordModal(true)}
                  disabled={!isEditing}
                >
                  변경
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>이름</label>
              <input 
                type="text" 
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                readOnly={!isEditing}
                className={!isEditing ? 'readonly-input' : ''}
              />
            </div>

            <div className="form-group">
              <label>전화번호</label>
              <input 
                type="tel" 
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                readOnly={!isEditing}
                className={!isEditing ? 'readonly-input' : ''}
                placeholder="010-1234-5678"
              />
            </div>

            <div className="form-group">
              <label>이메일</label>
              <input 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                readOnly={!isEditing}
                className={!isEditing ? 'readonly-input' : ''}
                placeholder="example@coderland.co.kr"
              />
            </div>

            
            {/* 경시 이력 - 관리자만 수정 가능, 튜터는 읽기 전용 */}
            {user.role !== 'admin' ? (
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="hasCompetition"
                    checked={formData.hasCompetition}
                    onChange={handleInputChange}
                    disabled={true}
                  />
                  <span className="checkmark"></span>
                  🏆 경시 이력
                </label>
              </div>
            ) : null}
          </div>
        </div>

        {/* 도움말 카드 */}
        <div className="profile-card help-card">
          <div className="card-header">
            <h3>💡 도움말</h3>
          </div>
          
          <div className="help-content">
            <div className="help-item">
              <strong>📞 문의사항:</strong>
              <p>계정 관련 문제가 있으시면 관리자에게 문의해 주세요.</p>
            </div>
          </div>
        </div>
      </div>

      {/* 비밀번호 변경 모달 */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="password-change-modal">
            <div className="modal-header">
              <h3>🔐 비밀번호 변경</h3>
              <button 
                className="close-btn"
                onClick={() => setShowPasswordModal(false)}
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label>현재 비밀번호 *</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="현재 비밀번호를 입력하세요"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label>새 비밀번호 *</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="새 비밀번호를 입력하세요"
                  disabled={loading}
                  minLength="6"
                  required
                />
                <small className="form-help">최소 6자 이상</small>
              </div>

              <div className="form-group">
                <label>새 비밀번호 확인 *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="새 비밀번호를 다시 입력하세요"
                  disabled={loading}
                  required
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={loading}
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                >
                  {loading ? '변경 중...' : '비밀번호 변경'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 커스텀 알림 모달 */}
      <CustomAlert {...alertState} />
    </div>
  );
};

export default MyProfile; 